import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.svg';

// Institution ID for College of Integrated Studies, Kuttiady
const INSTITUTION_ID = 39;
const INSTITUTION_NAME = "College of Integrated Studies, Kuttiady";

const AdmissionForm = () => {
	const navigate = useNavigate();

	// State Management
	const [formData, setFormData] = useState({});
	const [formFields, setFormFields] = useState([]);
	const [loading, setLoading] = useState(true);
	const [standards, setStandards] = useState([]);
	const [courses, setCourses] = useState([]);
	const [coursesType, setCoursesType] = useState(null);
	const [yearId, setYearId] = useState(null);
	const [institutionType, setInstitutionType] = useState(null);
	const [selectedStandard, setSelectedStandard] = useState('');
	const [selectedCourse, setSelectedCourse] = useState('');
	const [alert, setAlert] = useState({ message: '', type: '' });
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Alert Styling Function
	const alertStyle = (type) => ({
		backgroundColor: type === 'error' ? '#ffebee' : '#e8f5e9',
		color: type === 'error' ? '#c62828' : '#2e7d32',
		padding: '12px',
		borderRadius: '4px',
		marginBottom: '20px',
		fontSize: '14px',
		textAlign: 'center',
		fontWeight: '500',
		display: alert.message ? 'block' : 'none',
		border: `1px solid ${type === 'error' ? '#ef9a9a' : '#a5d6a7'}`
	});

	// Fetch Standards for Institution
	const handleStandards = async (institution_id) => {
		try {
			const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/school/standards/list/${institution_id}`, {
				method: "GET",
				headers: { "Content-Type": "application/json" }
			});
			const result = await response.json();
			setStandards(result.data);
		} catch (error) {
			console.error("Error fetching standards:", error);
			setAlert({ message: "Error fetching standards. Please try again.", type: 'error' });
		}
	};

	// Fetch courses for institution
	const handleCourses = async (institution_id) => {
		try {
			const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/list-institution-courses?institution_id=${institution_id}`, {
				method: "GET",
				headers: { "Content-Type": "application/json" }
			});
			const result = await response.json();
			setCourses(result.data);
		} catch (error) {
			console.error("Error fetching Courses:", error);
			setAlert({ message: "Error fetching Courses. Please try again.", type: 'error' });
		}
	};

	// Load form configuration on mount
	useEffect(() => {
		const loadFormConfig = async () => {
			try {
				const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/admission-application-config/${INSTITUTION_ID}`, {
					method: "GET",
					headers: { "Content-Type": "application/json" },
				});

				const res = await response.json();
				const institutionFormDetails = res.data;
				const institutionFormConfigs = institutionFormDetails.config;

				// Extract year_id and type from form configs
				const institutionTypeField = institutionFormConfigs.find(config => config.column_name === 'type');
				const yearIdField = institutionFormConfigs.find(config => config.column_name === 'year_id');
				const courseIdField = institutionFormConfigs.find(config => config.column_name === 'course_id');

				const courseValue = courseIdField ? courseIdField.type : null;
				const typeValue = institutionTypeField ? institutionTypeField.default_value : null;
				const yearValue = yearIdField ? yearIdField.default_value : null;

				setInstitutionType(typeValue);
				setCoursesType(courseValue);
				setYearId(yearValue);

				const fieldTypes = {
					str: 'text', standard: 'text', school: 'text',
					ration_card_type: 'text', yes_no: 'select',
					local_body: 'text', int: 'number', date: 'date'
				};

				const numberFields = [
					'mobile', 'whats_app', 'aadhaar', 'phone',
					'father_phone', 'mother_phone', 'pin_code'
				];

				const updatedFormFields = institutionFormConfigs
					.filter(config =>
						config.visibility === true &&
						!config.title.toLowerCase().includes('standard') &&
						!config.title.toLowerCase().includes('course')
					)
					.map(config => ({
						name: config.column_name,
						title: config.title,
						type: numberFields.includes(config.column_name) ? 'number' : fieldTypes[config.type] || 'text',
						required: config.is_required,
						defaultValue: config.default_value,
						isYesNo: config.type === 'yes_no'
					}));

				setFormFields(updatedFormFields);

				const initialFormData = {};
				updatedFormFields.forEach(field => {
					if (field.defaultValue !== null) {
						initialFormData[field.name] = field.isYesNo
							? (field.defaultValue ? "yes" : "no")
							: field.defaultValue;
					}
				});

				if (courseValue !== null) {
					setCoursesType(courseValue);
					await handleCourses(INSTITUTION_ID);
				} else {
					await handleStandards(INSTITUTION_ID);
				}
				setFormData(initialFormData);
			} catch (error) {
				console.error("Error fetching form config:", error);
				setAlert({ message: "Error loading form configuration. Please try again.", type: 'error' });
			} finally {
				setLoading(false);
			}
		};

		loadFormConfig();
	}, []);

	// Handle Form Input Changes
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	// Handle Form Submission
	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);

		// Build payload dynamically from all form fields
		const payload = {
			institution_id: Number(INSTITUTION_ID),
			standard_id: selectedStandard ? Number(selectedStandard) : null,
			course_id: selectedCourse ? Number(selectedCourse) : null,
			year_id: yearId ? Number(yearId) : null,
			type: institutionType || null,
		};

		// Add all form fields to payload
		Object.keys(formData).forEach(key => {
			let value = formData[key];

			// Handle empty strings as null
			if (value === '' || value === undefined) {
				value = null;
			}
			// Format date fields
			else if (key === 'dob' || key === 'tc_issue_date') {
				value = value ? new Date(value).toISOString().slice(0, 19).replace('T', ' ') : null;
			}
			// Convert yes/no to boolean
			else if (value === 'yes') {
				value = true;
			}
			else if (value === 'no') {
				value = false;
			}

			payload[key] = value;
		});

		try {
			const url = 'https://api.sirajulhuda.com/api/v1/erp/admission/application/create';
			const response = await fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			const result = await response.json();

			if (result.detail === "application existed") {
				setAlert({ message: 'Application already exists!', type: 'error' });
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} else {
				setAlert({
					message: 'Application submitted successfully! Redirecting...',
					type: 'success'
				});
				window.scrollTo({ top: 0, behavior: 'smooth' });

				setTimeout(() => {
					navigate('/success');
				}, 800);
			}

		} catch (error) {
			console.error("Error submitting form:", error);
			setAlert({ message: 'Error submitting application. Please try again.', type: 'error' });
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} finally {
			setIsSubmitting(false);
		}
	};

	// Loading state
	if (loading) {
		return (
			<div className="admission-page">
				<div className="form-container">
					<div className="form-wrapper">
						<div className="form-card">
							<img src={logo} className='logo-img' alt="" />
							<h2 className="form-title">Loading...</h2>
							<p style={{ textAlign: 'center' }}>Please wait while we load the admission form.</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Render Component
	return (
		<div className="admission-page">
			<div className="form-container">
				<div className="form-wrapper">
					<div className="form-card">
						<img src={logo} className='logo-img' alt="" />
						<h2 className="form-title"></h2>
						<p className="institution-name">Application for Admission For {INSTITUTION_NAME}</p>

						{/* Instructions */}
						<div className="box-items">
							<p className="selected-institutions">1. Fill the form in CAPITAL LETTERS only (English)</p>
							<p className="selected-institutions">2. Fields marked with red asterisk (*) are mandatory</p>
							<p className="selected-institutions">3. Ensure phone numbers and class are correct. Cannot be edited later</p>
							<p className="selected-institutions">4. Click Submit button after filling the complete form</p>
						</div>

						{/* Alert Message */}
						<div style={alertStyle(alert.type)}>
							{alert.message}
						</div>

						<form onSubmit={handleSubmit} className="admission-form">
							{/* Conditional Rendering for Standard or Course Selection */}
							{coursesType !== null ? (
								<div className="form-field">
									<label htmlFor="course" className="field-label">
										Select Course
										<span className="required">*</span>
									</label>
									<select
										id="course"
										value={selectedCourse}
										onChange={(e) => setSelectedCourse(e.target.value)}
										className="select-input"
										required
									>
										<option value="">Choose a Course</option>
										{courses.map((course) => (
											<option key={course.id} value={course.id}>
												{course.name}
											</option>
										))}
									</select>
								</div>
							) : (
								<div className="form-field">
									<label htmlFor="standard" className="field-label">
										Select Standard
										<span className="required">*</span>
									</label>
									<select
										id="standard"
										value={selectedStandard}
										onChange={(e) => setSelectedStandard(e.target.value)}
										className="select-input"
										required
									>
										<option value="">Choose a Standard</option>
										{standards.map((std) => (
											<option key={std.id} value={std.id}>
												{std.name}
											</option>
										))}
									</select>
								</div>
							)}

							<div className="form-fields">
								{formFields.map((field) => (
									<div key={field.name} className="form-field">
										<label htmlFor={field.name} className="field-label">
											{field.title}
											{field.required && <span className="required">*</span>}
										</label>
										{field.isYesNo ? (
											<select
												id={field.name}
												name={field.name}
												value={formData[field.name] || ''}
												onChange={handleChange}
												required={field.required}
												className="select-input"
											>
												<option value="">Select Yes/No</option>
												<option value="yes">Yes</option>
												<option value="no">No</option>
											</select>
										) : (
											<input
												type={field.type}
												id={field.name}
												name={field.name}
												required={field.required}
												value={formData[field.name] || ''}
												onChange={handleChange}
												className="field-input"
												placeholder={`Enter ${field.title}`}
											/>
										)}
									</div>
								))}
							</div>

							<button
								type="submit"
								className="submit-button"
								disabled={isSubmitting}
								style={{
									opacity: isSubmitting ? 0.7 : 1,
									cursor: isSubmitting ? 'not-allowed' : 'pointer'
								}}
							>
								{isSubmitting ? (
									<span className="loading-text">
										Submitting...
									</span>
								) : (
									'Submit Application'
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdmissionForm;
